import React, { useEffect, useState } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const EditPricing = ({
  handleEdit,
  selectedTableType, // e.g. 'sqft' or 'accommodation'
  singlePricingData, // data from the server
  pricingType, // e.g. 'sqft' or 'accommodation'
  selectedAccommodationType, // e.g. 'INDEPENDENT' or 'GROUP'
  onClose,
  isSubmitting
}) => {
  const userId = Cookies.get('userId');
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm();

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Pricing Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (!singlePricingData) return;

    const lf = singlePricingData.loading_factor ?? '';
    setLoadingFactor(String(lf));

    if (selectedAccommodationType === 'INDEPENDENT') {
      reset({
        plote_price: singlePricingData.plote_price || '',
        construction_price: singlePricingData.construction_price || '',
        loading_factor: lf
      });
    } else {
      reset({
        rate: singlePricingData.price || '',
        loading_factor: lf
      });
    }
  }, [singlePricingData, selectedAccommodationType, reset]);

  const onSubmit = (data) => {
    const processedData = {
      accommodation_id: singlePricingData.accommodation_id,
      project_id: singlePricingData.project_id,
      mode: selectedTableType === 'sqft' ? 'sqft' : 'accommodation'
    };

    if (selectedAccommodationType === 'INDEPENDENT') {
      processedData.plote_price = parseInt(data.plote_price, 10) || 0;
      processedData.construction_price = parseInt(data.construction_price, 10) || 0;
      if (processedData.mode === 'sqft') {
        processedData.loading_factor = parseFloat(data.loading_factor, 10) || 0;
      }
    } else {
      processedData.price = parseInt(data.rate, 10) || 0;
      if (processedData.mode === 'sqft') {
        processedData.loading_factor = parseFloat(data.loading_factor, 10) || 0;
      }
    }

    handleEdit(processedData);
  };

  const [loadingFactor, setLoadingFactor] = useState('');

  const handleLoadingFactorChange = (e) => {
    let inputVal = e;
    inputVal = inputVal.replace(/\D/g, '');
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Loading factor cannot exceed 100');
    }
    setLoadingFactor(inputVal);
    setValue('loading_factor', inputVal);
  };

  const accommodation = singlePricingData?.accommodation;
  const rateTypeLabel =
    selectedTableType === 'sqft'
      ? `${measurementType} Based`
      : 'Accommodation Based';
  const buildUpArea =
    accommodation?.buildUpArea ?? accommodation?.build_up_area ?? '—';
  const plotArea =
    accommodation?.plotArea ?? accommodation?.plot_area ?? '—';

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
              Rate Details
            </span>
          </div>

          {/* Rate Type, Accommodation Type, Accommodation Name - one row (read-only like Create) */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Type
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {rateTypeLabel}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Type
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {selectedAccommodationType || '—'}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Name
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {accommodation?.name ?? '—'}
              </div>
            </div>
          </div>

          {/* Build up area & Plot area - same as Create Rate List */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Build up area
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {buildUpArea}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plot area
              </label>
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                {plotArea}
              </div>
            </div>
          </div>

          {/* Loading factor - when sqft */}
          {selectedTableType === 'sqft' && (
            <div className="flex justify-between mt-4">
              <FormInput
                width={49}
                label="Loading Factor (%)"
                register={register}
                register_key="loading_factor"
                type="text"
                onType={handleLoadingFactorChange}
                defaultValuePre={loadingFactor}
                errors={errors.loading_factor}
              />
            </div>
          )}

          {/* Editable rate fields - same layout as Create */}
          <div className="flex w-full justify-between mt-4">
            {selectedAccommodationType === 'INDEPENDENT' ? (
              <div className="flex justify-between mt-4 w-full gap-4">
                <FormInput
                  width={49}
                  label="Plot Price"
                  register={register}
                  register_key="plote_price"
                  isRequired
                  type="text"
                />
                <FormInput
                  width={49}
                  label="Construction Price"
                  register={register}
                  register_key="construction_price"
                  type="text"
                />
              </div>
            ) : (
              <FormInput
                width={49}
                label={
                  selectedTableType === 'sqft'
                    ? `Rate per ${measurementType}`
                    : 'Price'
                }
                register_key="rate"
                isRequired
                register={register}
                type="text"
              />
            )}
          </div>

          {/* Buttons - same as Create Rate List */}
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button type="button" displayType="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditPricing;
